// ============================================================
// Hirafi — Module devis : éditeur de lignes de prestation
// ============================================================

import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { QuoteLineItem } from "../types";
import { calcLineTotal } from "../lib/calculations";

type LineItemDraft = Omit<QuoteLineItem, "id" | "quote_id">;

interface Props {
  items: LineItemDraft[];
  onChange: (items: LineItemDraft[]) => void;
}

export function LineItemsEditor({ items, onChange }: Props) {
  const addLine = () => {
    onChange([
      ...items,
      { label: "", quantity: 1, unit: "service", unit_price: 0, total_price: 0 },
    ]);
  };

  const update = (idx: number, field: keyof LineItemDraft, raw: string) => {
    const updated = items.map((item, i) => {
      if (i !== idx) return item;
      const next = { ...item, [field]: field === "label" || field === "unit" ? raw : parseFloat(raw) || 0 };
      if (field === "quantity" || field === "unit_price") {
        next.total_price = calcLineTotal(
          field === "quantity" ? next.quantity : item.quantity,
          field === "unit_price" ? next.unit_price : item.unit_price
        );
      }
      return next;
    });
    onChange(updated);
  };

  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div key={idx} className="grid grid-cols-[1fr_72px_100px_88px_40px] gap-2 items-end">
          <div>
            {idx === 0 && (
              <p className="text-xs text-muted-foreground mb-1 font-medium">Prestation</p>
            )}
            <Input
              value={item.label}
              onChange={(e) => update(idx, "label", e.target.value)}
              placeholder="Ex: Remplacement joint"
              className="h-9 text-sm"
            />
          </div>
          <div>
            {idx === 0 && (
              <p className="text-xs text-muted-foreground mb-1 font-medium">Qté</p>
            )}
            <Input
              type="number"
              min={1}
              value={item.quantity}
              onChange={(e) => update(idx, "quantity", e.target.value)}
              className="h-9 text-sm text-center"
            />
          </div>
          <div>
            {idx === 0 && (
              <p className="text-xs text-muted-foreground mb-1 font-medium">Prix unit.</p>
            )}
            <Input
              type="number"
              min={0}
              value={item.unit_price}
              onChange={(e) => update(idx, "unit_price", e.target.value)}
              className="h-9 text-sm"
            />
          </div>
          <div>
            {idx === 0 && (
              <p className="text-xs text-muted-foreground mb-1 font-medium">Total</p>
            )}
            <Input
              value={item.total_price.toFixed(0)}
              readOnly
              tabIndex={-1}
              className="h-9 text-sm bg-muted/50 cursor-default font-medium"
            />
          </div>
          <div className={idx === 0 ? "mt-5" : ""}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground hover:text-destructive"
              onClick={() => remove(idx)}
              aria-label="Supprimer la ligne"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5 text-muted-foreground"
        onClick={addLine}
      >
        <Plus className="h-3.5 w-3.5" />
        Ajouter une prestation
      </Button>
    </div>
  );
}
